import { Ohje } from "./Ohje";
import { useCallback, useEffect, useMemo, useState } from "react";
import { naytaPvm } from "./pvm";
import {
  api, ApiError,
  type Kayttaja, type TarjousAsetukset as Asetukset,
  type Tes, type TesLisalaji, type TesYksikko, type TesEhdokas,
  type Hitsausnormi,
} from "./api";
import { tanaan as paikallinenPaiva } from "./api";
import { t, esim, virheTeksti, type KaannosAvain } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Tarjouslaskennan asetukset — talon omat luvut.

   Tarjouslaskennan RAKENNE on koodissa; LUVUT täytetään täällä.
   Sivukulut eritellään komponentteina — efektiivinen kerroin
   lasketaan niistä (1 + Σ%/100), ei tallenneta erikseen. Sama
   kaava täällä ja laskennassa: yksi totuus per suure.

   Pääkäyttäjän näkymä: nämä ovat hinnoittelun perusdataa.
   ═══════════════════════════════════════════════════════════ */

/* Nimet avaimina: moduulin tasolla t() jäädyttäisi kielen lataushetkeen. */
const LISA_AVAIN: Record<TesLisalaji, KaannosAvain> = {
  ilta:      "tas.lisa_ilta",
  yo:        "tas.lisa_yo",
  lauantai:  "tas.lisa_lauantai",
  sunnuntai: "tas.lisa_sunnuntai",
  ylityo50:  "tas.lisa_ylityo50",
  ylityo100: "tas.lisa_ylityo100",
};
const lisaNimi = (l: TesLisalaji): string => t(LISA_AVAIN[l]);
// Kunkin lajin luonnollinen yksikkö TES:ssä (vuorolisät €/h, korotukset %).
const LISA_YKSIKKO: Record<TesLisalaji, TesYksikko> = {
  ilta: "eur_per_h", yo: "eur_per_h", lauantai: "eur_per_h",
  sunnuntai: "prosentti", ylityo50: "prosentti", ylityo100: "prosentti",
};
const LAJI_JARJESTYS: TesLisalaji[] =
  ["ilta", "yo", "lauantai", "sunnuntai", "ylityo50", "ylityo100"];
const yksikkoMerkki = (y: TesYksikko) => (y === "eur_per_h" ? "€/h" : "%");

const num = (n: number | null | undefined, d = 2) =>
  n == null ? "" : n.toLocaleString("fi-FI", { maximumFractionDigits: d });

/* Sivukulujen tyypilliset suuruusluokat — VIHJE, ei tallennettua dataa.
   Suuntaa-antavia haarukoita: lakisääteiset muuttuvat vuosittain,
   tapaturma on riskiluokan mukaan (hitsaus korkea) ja osa on TES-
   sidonnaista. Talo säätää omat lukunsa; nämä vain estävät tyhjän
   nollan tuijottamisen. Ei väitetä totuudeksi. */
const SIVUKULU_VIHJE: Record<string, KaannosAvain> = {
  tyel:       "tas.vihje_tyel",
  sotu:       "tas.vihje_sotu",
  tyottomyys: "tas.vihje_tyottomyys",
  tapaturma:  "tas.vihje_tapaturma",
  lomapalkka: "tas.vihje_lomapalkka",
  arkipyha:   "tas.vihje_arkipyha",
};

/* Numerokenttä joka tallentaa vasta kun fokus lähtee (tai Enter).
   Ei tallenna jokaisesta näppäinpainalluksesta — vähemmän kutsuja,
   ei puolivalmiita arvoja kantaan. */
function LukuKentta({
  arvo, yksikko, leveys, salliTyhja, min, max, onTallenna,
}: {
  arvo: number | null;
  yksikko?: string;
  leveys?: number;
  salliTyhja?: boolean;
  min?: number;
  max?: number;
  onTallenna: (arvo: number | null) => Promise<unknown>;
}) {
  const [teksti, setTeksti] = useState(arvo == null ? "" : String(arvo));
  const [tila, setTila] = useState<"" | "tallennetaan" | "ok" | "virhe">("");

  useEffect(() => { setTeksti(arvo == null ? "" : String(arvo)); }, [arvo]);

  const tallenna = async () => {
    const tx = teksti.trim().replace(",", ".");
    let uusi: number | null;
    if (tx === "") {
      if (!salliTyhja) { setTeksti(arvo == null ? "" : String(arvo)); return; }
      uusi = null;
    } else {
      const n = Number(tx);
      if (!Number.isFinite(n) || (min != null && n < min) || (max != null && n > max)) {
        setTila("virhe"); return;
      }
      uusi = n;
    }
    if (uusi === arvo) { setTila(""); return; }
    setTila("tallennetaan");
    try { await onTallenna(uusi); setTila("ok"); }
    catch { setTila("virhe"); }
  };

  return (
    <span className="lukukentta">
      <input
        className={`lukukentta__syote${tila === "virhe" ? " lukukentta__syote--virhe" : ""}`}
        inputMode="decimal"
        style={leveys ? { width: leveys } : undefined}
        value={teksti}
        onChange={(e) => { setTeksti(e.target.value); setTila(""); }}
        onBlur={tallenna}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      />
      {yksikko && <span className="lukukentta__yksikko">{yksikko}</span>}
      {tila === "tallennetaan" && <span className="lukukentta__tila">…</span>}
      {tila === "ok" && <span className="lukukentta__tila lukukentta__tila--ok">✓</span>}
      {tila === "virhe" && <span className="lukukentta__tila lukukentta__tila--virhe">{t("tas.virhe")}</span>}
    </span>
  );
}

export default function Tarjousasetukset({ kayttaja }: { kayttaja: Kayttaja }) {
  const saaMuokata = kayttaja.roolit.includes("paakayttaja");
  const [d, setD] = useState<Asetukset | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);

  const lataa = useCallback(async () => {
    try { setD(await api.tarjousAsetukset()); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setD(null); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  if (!saaMuokata) {
    return (
      <section className="panel">
        <div className="panel__otsikko"><h2>{t("tas.otsikko")}</h2></div>
        <p className="dim">{t("yri.vain_paakayttaja")}</p>
      </section>
    );
  }

  return (
    <>
      <header className="head">
        <div className="head__rivi">
          <h1>{t("tas.otsikko")}</h1>
          <span className="dim">{t("tas.alaotsikko")}</span>
        </div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: Asetusreitit vaadiRooli(paakayttaja) + moduuli tarjous; vuokra_kerroin ei ole laskennassa (mitattu 24.9.). */}
      <Ohje
        kuka={<>{t("tas.ohje.kuka")}</>}
        luvut={<>{t("tas.ohje.luvut")}</>} />
    <section className="panel asetukset">
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      {d === null && !virhe && <p className="dim">{t("yleinen.ladataan")}</p>}

      {d && (
        <div className="asetukset__grid">
          <div className="asetukset__palsta">
          {/* ── Sivukulut ── */}
          <div className="asetukset__lohko">
            <h3>{t("tas.sivukulut")}</h3>
            <p className="dim">
              {t("tas.sivukulut_selite")}
            </p>
            <table className="asetukset__taulu">
              <tbody>
                {d.sivukulut.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.nimi}
                      {s.vuosi && <span className="asetukset__vihje" style={{ fontWeight: 700 }}>{s.vuosi}</span>}
                      {SIVUKULU_VIHJE[s.tunnus] && (
                        <span className="asetukset__vihje">{t(SIVUKULU_VIHJE[s.tunnus]!)}</span>
                      )}
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, fontSize: 11 }}>
                        <label className="dim">{t("tas.vuosi")}{" "}
                          <input type="number" min={2000} max={2100} defaultValue={s.vuosi ?? ""} style={{ width: 84 }}
                            onBlur={(e) => {
                              const v = e.target.value ? Number(e.target.value) : null;
                              if (v !== s.vuosi) void api.paivitaSivukuluLahde(s.id, { vuosi: v }).then(lataa);
                            }} />
                        </label>
                        <label className="dim" style={{ flex: 1 }}>{t("tas.lahde_otsikko")}
                        <input type="url" placeholder={t("tas.lahde_url")}
                          defaultValue={s.lahde_url ?? ""} style={{ flex: 1, minWidth: 160 }}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (s.lahde_url ?? "")) void api.paivitaSivukuluLahde(s.id, { lahde_url: v || null }).then(lataa);
                          }} /></label>
                        {s.lahde_url && <a href={s.lahde_url} target="_blank" rel="noreferrer">{t("tas.avaa_lahde")}</a>}
                      </div>
                    </td>
                    <td className="asetukset__syote">
                      <LukuKentta
                        arvo={s.prosentti} yksikko="%" leveys={70} min={0} max={1000}
                        onTallenna={async (v) => {
                          await api.paivitaSivukulu(s.id, v ?? 0);
                          await lataa(); // kerroin lasketaan uudelleen
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="asetukset__summa">
                  <td>{t("tas.kerroin")}</td>
                  <td className="mono">× {num(d.kerroin, 4)}</td>
                </tr>
              </tfoot>
            </table>
            <p className="dim" style={{ marginTop: 8 }}>
              {t("tas.lakisaateiset_1")} <b>{t("tas.lakisaateiset_2")}</b>. {t("tas.lakisaateiset_3")}
            </p>
          </div>

          {/* ── Osaamisluokkien tuntihinnat ── */}
          <div className="asetukset__lohko">
            <h3>{t("tas.palkat")}</h3>
            <p className="dim">{t("tas.syota")} <b>{t("tas.raakapalkka_eur_h")}</b>{t("tas.syota_loppu")}</p>
            <p className="dim">
              {t("tas.palkat_selite")}
            </p>
            <table className="asetukset__taulu">
              <thead>
                <tr><th></th><th>{t("tas.s_raakapalkka")}</th><th>{t("tas.s_matkat")}</th></tr>
              </thead>
              <tbody>
                {d.osaamisluokat.map((o) => (
                  <tr key={o.id}>
                    <td>{o.nimi}</td>
                    <td className="asetukset__syote">
                      <LukuKentta
                        arvo={o.tuntihinta} yksikko="€/h" leveys={80} salliTyhja min={0} max={9999}
                        onTallenna={(v) => api.paivitaOsaamisluokkaHinta(o.id, v)}
                      />
                    </td>
                    <td className="asetukset__syote">
                      <LukuKentta
                        arvo={o.matkat_per_kk} yksikko={t("tas.per_kk")} leveys={56} min={0} max={99}
                        onTallenna={(v) => api.paivitaOsaamisluokkaMatkat(o.id, v ?? 0)}
                      />
                    </td>
                  </tr>
                ))}
                {d.osaamisluokat.length === 0 && (
                  <tr><td className="dim" colSpan={3}>{t("tas.ei_osaamisluokkia")}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Laskenta-asetukset ── */}
          <div className="asetukset__lohko">
            <h3>{t("tas.oletukset")}</h3>
            <table className="asetukset__taulu">
              <tbody>
                <tr>
                  <td>{t("tas.vuokra_kerroin")}</td>
                  <td className="asetukset__syote">
                    <LukuKentta
                      arvo={d.asetukset.vuokra_kerroin} yksikko="×" leveys={70} min={0} max={100}
                      onTallenna={(v) => api.paivitaTarjousAsetukset({ vuokra_kerroin: v ?? 0 })}
                    />
                    <span className="dim"> {t("tas.vuokra_kaava")}</span>
                  </td>
                </tr>
                <tr>
                  <td>{t("tas.tyopaivia")}</td>
                  <td className="asetukset__syote">
                    <LukuKentta
                      arvo={d.asetukset.tyopaivia_per_vko} yksikko={t("tas.vrk")} leveys={60} min={1} max={7}
                      onTallenna={(v) => api.paivitaTarjousAsetukset({ tyopaivia_per_vko: v ?? 6 })}
                    />
                  </td>
                </tr>
                <tr>
                  <td>{t("tas.tunteja")}</td>
                  <td className="asetukset__syote">
                    <LukuKentta
                      arvo={d.asetukset.tunnit_per_paiva} yksikko="h" leveys={60} min={1} max={24}
                      onTallenna={(v) => api.paivitaTarjousAsetukset({ tunnit_per_paiva: v ?? 8 })}
                    />
                    <span className="dim"> {t("tas.tunteja_esim")}</span>
                  </td>
                </tr>
                <tr>
                  <td>{t("tas.matkakustannus")}</td>
                  <td className="asetukset__syote">
                    <LukuKentta
                      arvo={d.asetukset.matka_kustannus} yksikko="€" leveys={80} min={0} max={100000}
                      onTallenna={(v) => api.paivitaTarjousAsetukset({ matka_kustannus: v ?? 0 })}
                    />
                  </td>
                </tr>
                <tr>
                  <td>{t("tas.paivaraha")}</td>
                  <td className="asetukset__syote">
                    <LukuKentta
                      arvo={d.asetukset.matka_paivaraha} yksikko="€" leveys={80} min={0} max={100000}
                      onTallenna={(v) => api.paivitaTarjousAsetukset({ matka_paivaraha: v ?? 0 })}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── TES + lisät ── */}
          <TesLohko tesit={d.tesit} onMuutos={lataa} />

          {/* ── Normitaulukko (DN → h/hitsi) ── */}
          <NormiLohko />
          </div>

          <aside className="asetukset__sivu">
            <div className="asetukset__kerroin-laatikko">
              <span className="tunnusluku__otsikko">{t("tas.kerroin")}</span>
              <b>× {num(d.kerroin, 4)}</b>
              <p className="dim">
                {t("tas.kerroin_selite")}
              </p>
            </div>
          </aside>
        </div>
      )}
    </section>
    </>
  );
}

/* Normitaulukko: DN → h/hitsi. Talo täyttää; erä ehdottaa normin DN:stä. */
function NormiLohko() {
  const [rivit, setRivit] = useState<Hitsausnormi[] | null>(null);
  const [uusiAuki, setUusiAuki] = useState(false);
  const [dn, setDn] = useState("");
  const [prosessi, setProsessi] = useState("");
  const [normi, setNormi] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);

  const lataa = useCallback(async () => {
    try { setRivit(await api.hitsausnormit()); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setRivit([]); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  const lisaa = async () => {
    const d = Number(dn), n = Number(normi.replace(",", "."));
    if (!(d > 0)) { setVirhe(t("tas.anna_dn")); return; }
    if (!(n >= 0)) { setVirhe(t("tas.anna_normi")); return; }
    try {
      await api.lisaaHitsausnormi({ dn: d, hitsausprosessi: prosessi.trim() || null, normi_h: n });
      setDn(""); setProsessi(""); setNormi(""); setUusiAuki(false); setVirhe(null);
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.lisays_virhe")); }
  };

  return (
    <div className="asetukset__lohko">
      <div className="asetukset__lohko-otsikko">
        <h3>{t("tas.normitaulukko")}</h3>
        <button type="button" className="nappi nappi--kevyt" onClick={() => setUusiAuki((v) => !v)}>
          {uusiAuki ? t("hl.peruuta") : t("tas.uusi_rivi")}
        </button>
      </div>
      <p className="dim">
        {t("tas.normi_selite")}
      </p>

      {uusiAuki && (
        <div className="asetukset__uusi">
          <label>DN<input placeholder={esim("100")} value={dn} inputMode="numeric" style={{ width: 90 }}
                 onChange={(e) => setDn(e.target.value)} /></label>
          <label>{t("tas.prosessi")}<input placeholder={esim("141")} value={prosessi} maxLength={20}
                 onChange={(e) => setProsessi(e.target.value)} /></label>
          <label>{t("tas.h_hitsi")}<input placeholder={esim("1,5")} value={normi} inputMode="decimal" style={{ width: 100 }}
                 onChange={(e) => setNormi(e.target.value)} /></label>
          <button type="button" className="nappi nappi--paa" onClick={lisaa}>{t("yri.lisaa")}</button>
          {virhe && <span className="huom">{virhe}</span>}
        </div>
      )}

      {rivit && rivit.length > 0 && (
        <table className="asetukset__taulu">
          <thead>
            <tr><th>DN</th><th>{t("tas.prosessi")}</th><th>{t("tas.h_hitsi")}</th><th></th></tr>
          </thead>
          <tbody>
            {rivit.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.dn}</td>
                <td className="dim">{r.hitsausprosessi ?? t("tas.kaikki_prosessit")}</td>
                <td className="asetukset__syote">
                  <LukuKentta arvo={r.normi_h} yksikko="h" leveys={70} min={0} max={9999}
                              onTallenna={(v) => api.paivitaHitsausnormi(r.id, v ?? 0)} />
                </td>
                <td>
                  <button type="button" className="asetukset__poista" title={t("tas.poista_rivi")}
                          onClick={async () => { await api.poistaHitsausnormi(r.id); await lataa(); }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rivit && rivit.length === 0 && <p className="dim">{t("tas.ei_normeja")}</p>}
    </div>
  );
}

/* TESit lisineen. Lisät ovat päivämäärällisiä versioita — laskenta
   poimii päivälle uusimman voimassa olevan. Uusi jakso ei ylikirjoita
   vanhaa: voimaantulopäivä kertoo mistä alkaen se pätee. */
function TesLohko({
  tesit, onMuutos,
}: {
  tesit: Tes[];
  onMuutos: () => Promise<void>;
}) {
  const [uusiAuki, setUusiAuki] = useState(false);
  const [tunnus, setTunnus] = useState("");
  const [nimi, setNimi] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);

  const luo = async () => {
    if (!tunnus.trim() || !nimi.trim()) return;
    try {
      await api.luoTes(tunnus.trim(), nimi.trim());
      setTunnus(""); setNimi(""); setUusiAuki(false); setVirhe(null);
      await onMuutos();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.lisays_virhe")); }
  };

  return (
    <div className="asetukset__lohko">
      <div className="asetukset__lohko-otsikko">
        <h3>{t("tas.tes_otsikko")}</h3>
        <button type="button" className="nappi nappi--kevyt" onClick={() => setUusiAuki((v) => !v)}>
          {uusiAuki ? t("hl.peruuta") : t("tas.uusi_tes")}
        </button>
      </div>
      <p className="dim">
        {t("tas.tes_selite")}
      </p>

      {uusiAuki && (
        <div className="asetukset__uusi">
          <input placeholder={t("tas.tunnus_esim")} value={tunnus}
                 onChange={(e) => setTunnus(e.target.value)} maxLength={40} />
          <input placeholder={t("iv.nimi")} value={nimi}
                 onChange={(e) => setNimi(e.target.value)} maxLength={120} />
          <button type="button" className="nappi nappi--paa" onClick={luo}>{t("yri.lisaa")}</button>
          {virhe && <span className="huom">{virhe}</span>}
        </div>
      )}

      {tesit.map((te) => <TesKortti key={te.id} tes={te} onMuutos={onMuutos} />)}
      {tesit.length === 0 && <p className="dim">{t("tas.ei_tesja")}</p>}
    </div>
  );
}

/* Yhden TESin lisät: voimassa olevat arvot + jaksohistoria + uusi jakso. */
function TesKortti({ tes, onMuutos }: { tes: Tes; onMuutos: () => Promise<void> }) {
  // Toni 15.10.2026 klo 19.30: paikallinen päivä yhteisestä apufunktiosta. toISOString on UTC-päivä, joka on Suomessa klo 00–03 eilinen.
  const tanaan = useMemo(() => paikallinenPaiva(), []);
  const [jaksoAuki, setJaksoAuki] = useState(false);
  const [tuontiAuki, setTuontiAuki] = useState(false);
  const [alkaa, setAlkaa] = useState(tanaan);
  const [arvot, setArvot] = useState<Record<string, string>>({});
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallentaa, setTallentaa] = useState(false);

  // Voimassa oleva versio per laji tänään: uusin alkaa <= tänään.
  const voimassa = useMemo(() => {
    const vo: Partial<Record<TesLisalaji, { arvo: number; yksikko: TesYksikko; alkaa: string }>> = {};
    for (const l of tes.lisat) {
      if (l.alkaa <= tanaan) {
        const n = vo[l.laji];
        if (!n || l.alkaa > n.alkaa) vo[l.laji] = { arvo: l.arvo, yksikko: l.yksikko, alkaa: l.alkaa };
      }
    }
    return vo;
  }, [tes.lisat, tanaan]);

  // Erilliset jaksot (voimaantulopäivät) uusin ensin.
  const jaksot = useMemo(
    () => [...new Set(tes.lisat.map((l) => l.alkaa))].sort().reverse(),
    [tes.lisat]
  );

  const avaaJakso = () => {
    // Esitäytä voimassa olevilla arvoilla — muokataan vain muuttuneet.
    const pohja: Record<string, string> = {};
    for (const laji of LAJI_JARJESTYS) {
      const v = voimassa[laji];
      pohja[laji] = v ? String(v.arvo) : "";
    }
    setArvot(pohja); setAlkaa(tanaan); setVirhe(null); setJaksoAuki(true);
  };

  const tallennaJakso = async () => {
    const rivit = LAJI_JARJESTYS
      .filter((laji) => arvot[laji]?.trim() !== "" && arvot[laji] != null)
      .map((laji) => ({
        laji, yksikko: LISA_YKSIKKO[laji],
        arvo: Number(arvot[laji]!.replace(",", ".")),
      }))
      .filter((r) => Number.isFinite(r.arvo));
    if (rivit.length === 0) { setVirhe(t("tas.anna_arvo")); return; }
    setTallentaa(true);
    try {
      await api.tallennaTesJakso(tes.id, alkaa, rivit);
      setJaksoAuki(false); setVirhe(null);
      await onMuutos();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallentaa(false); }
  };

  const poistaJakso = async (pvm: string) => {
    try { await api.poistaTesJakso(tes.id, pvm); await onMuutos(); }
    catch { /* näkyy seuraavassa latauksessa */ }
  };

  return (
    <div className={`asetukset__tes${tes.aktiivinen ? "" : " asetukset__tes--pois"}`}>
      <div className="asetukset__tes-otsikko">
        <b>{tes.nimi}</b> <span className="dim mono">{tes.tunnus}</span>
        {!tes.aktiivinen && <span className="dim"> · {t("tas.ei_kaytossa")}</span>}
        <button type="button" className="nappi nappi--kevyt asetukset__oikealle"
                onClick={() => { setTuontiAuki((v) => !v); setJaksoAuki(false); }}>
          {tuontiAuki ? t("tas.sulje_tuonti") : t("tas.hae_pdf")}
        </button>
        <button type="button" className="nappi nappi--kevyt"
                onClick={jaksoAuki ? () => setJaksoAuki(false) : () => { avaaJakso(); setTuontiAuki(false); }}>
          {jaksoAuki ? t("hl.peruuta") : t("tas.uusi_jakso")}
        </button>
      </div>

      {tuontiAuki && (
        <TesPdfTuonti tesId={tes.id}
          onValmis={async () => { setTuontiAuki(false); await onMuutos(); }} />
      )}

      {/* Voimassa olevat arvot tänään */}
      <table className="asetukset__taulu asetukset__lisataulu">
        <tbody>
          {LAJI_JARJESTYS.map((laji) => {
            const v = voimassa[laji];
            return (
              <tr key={laji}>
                <td>{lisaNimi(laji)}</td>
                <td className="mono">
                  {v ? `${num(v.arvo, 2)} ${yksikkoMerkki(v.yksikko)}` : <span className="dim">—</span>}
                </td>
                <td className="dim asetukset__pvm">
                  {v ? t("tas.voimassa_alkaen", { pvm: naytaPvm(v.alkaa) ?? "" }) : `(${yksikkoMerkki(LISA_YKSIKKO[laji])})`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Uusi jakso -lomake */}
      {jaksoAuki && (
        <div className="asetukset__jakso">
          <label className="asetukset__jakso-pvm">
            {t("tas.voimaantulopaiva")}{" "}
            <input type="date" value={alkaa} onChange={(e) => setAlkaa(e.target.value)} />
          </label>
          <div className="asetukset__lisat">
            {LAJI_JARJESTYS.map((laji) => (
              <label key={laji} className="asetukset__lisa">
                <span>{lisaNimi(laji)} <span className="dim">({yksikkoMerkki(LISA_YKSIKKO[laji])})</span></span>
                <input className="lukukentta__syote" inputMode="decimal" style={{ width: 72 }}
                       value={arvot[laji] ?? ""}
                       onChange={(e) => setArvot((a) => ({ ...a, [laji]: e.target.value }))} />
              </label>
            ))}
          </div>
          <div className="asetukset__jakso-toiminnot">
            <button type="button" className="nappi nappi--paa" disabled={tallentaa} onClick={tallennaJakso}>
              {tallentaa ? t("pu.tallennetaan") : t("tas.tallenna_jakso")}
            </button>
            {virhe && <span className="huom">{virhe}</span>}
          </div>
        </div>
      )}

      {/* Jaksohistoria */}
      {jaksot.length > 0 && (
        <div className="asetukset__jaksot dim">
          {t("tas.jaksot")}{" "}
          {jaksot.map((pvm) => (
            <span key={pvm} className="asetukset__jakso-merkki">
              {naytaPvm(pvm)}
              <button type="button" className="asetukset__poista" title={t("tas.poista_jakso")}
                      onClick={() => poistaJakso(pvm)}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* Lukee tiedoston base64:ksi (data-URL-etuliite pois). */
function lueBase64(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => rej(new Error(t("mat.luku_virhe")));
    r.readAsDataURL(f);
  });
}

type TuontiRivi = TesEhdokas & { hyvaksy: boolean; alkaaEdit: string; arvoEdit: string };

/* TES-PDF:n tuonti: poimi → tarkista lähdettä vasten → vahvista.
   Mitään ei tallenneta ennen kuin käyttäjä painaa Tallenna. */
function TesPdfTuonti({ tesId, onValmis }: { tesId: number; onValmis: () => Promise<void> }) {
  const [rivit, setRivit] = useState<TuontiRivi[] | null>(null);
  const [lataa, setLataa] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallentaa, setTallentaa] = useState(false);

  async function valitseTiedosto(f: File | undefined) {
    if (!f) return;
    setLataa(true); setVirhe(null); setRivit(null);
    try {
      const sisalto = await lueBase64(f);
      const { ehdokkaat } = await api.parsiTes(sisalto);
      if (ehdokkaat.length === 0) setVirhe(t("tas.ei_lisia"));
      setRivit(ehdokkaat.map((e) => ({
        ...e, hyvaksy: true, alkaaEdit: e.alkaa ?? "", arvoEdit: String(e.arvo),
      })));
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tas.poiminta_virhe")); }
    finally { setLataa(false); }
  }

  const paivita = (i: number, muutos: Partial<TuontiRivi>) =>
    setRivit((rs) => rs ? rs.map((r, j) => (j === i ? { ...r, ...muutos } : r)) : rs);

  async function tallenna() {
    if (!rivit) return;
    const valitut = rivit.filter((r) => r.hyvaksy);
    if (valitut.length === 0) { setVirhe(t("tas.valitse_rivi")); return; }
    if (valitut.some((r) => !r.alkaaEdit)) {
      setVirhe(t("tas.aseta_paiva")); return;
    }
    // Ryhmittele voimaantulopäivän mukaan → yksi jakso per päivä.
    const jaksot = new Map<string, { laji: string; yksikko: string; arvo: number }[]>();
    for (const r of valitut) {
      const arvo = Number(r.arvoEdit.replace(",", "."));
      if (!Number.isFinite(arvo)) continue;
      const lista = jaksot.get(r.alkaaEdit) ?? [];
      lista.push({ laji: r.laji, yksikko: r.yksikko, arvo });
      jaksot.set(r.alkaaEdit, lista);
    }
    setTallentaa(true); setVirhe(null);
    try {
      for (const [pvm, arvot] of jaksot) await api.tallennaTesJakso(tesId, pvm, arvot);
      await onValmis();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallentaa(false); }
  }

  return (
    <div className="tespdf">
      <p className="dim">
        {t("tas.pdf_ohje")}
      </p>
      <input type="file" accept="application/pdf,.pdf"
             onChange={(e) => void valitseTiedosto(e.target.files?.[0])} />
      {lataa && <p className="dim">{t("tas.puretaan")}</p>}
      {virhe && <p className="huom" role="alert">{virhe}</p>}

      {rivit && rivit.length > 0 && (
        <>
          <table className="tespdf__taulu">
            <thead>
              <tr><th></th><th>{t("tas.s_lisa")}</th><th>{t("tas.s_arvo")}</th><th>{t("tas.s_voimaan")}</th><th>{t("tas.s_varmuus")}</th><th>{t("tas.s_lahde")}</th></tr>
            </thead>
            <tbody>
              {rivit.map((r, i) => (
                <tr key={i} className={r.varmuus === "matala" ? "tespdf__matala" : ""}>
                  <td>
                    <input type="checkbox" checked={r.hyvaksy}
                           onChange={(e) => paivita(i, { hyvaksy: e.target.checked })} />
                  </td>
                  <td>{lisaNimi(r.laji)}</td>
                  <td className="mono tespdf__arvo">
                    <input className="lukukentta__syote" style={{ width: 60 }} value={r.arvoEdit}
                           onChange={(e) => paivita(i, { arvoEdit: e.target.value })} />
                    {" "}{yksikkoMerkki(r.yksikko)}
                  </td>
                  <td>
                    <input type="date" value={r.alkaaEdit}
                           onChange={(e) => paivita(i, { alkaaEdit: e.target.value })} />
                    {!r.alkaa && <span title={t("tas.voimaan_tullessa")}> ⚑</span>}
                  </td>
                  <td><span className={"tespdf__varmuus tespdf__varmuus--" + r.varmuus}>{t(r.varmuus === "korkea" ? "tas.varmuus_korkea" : "tas.varmuus_matala")}</span></td>
                  <td className="dim tespdf__lahde" title={r.lahde}>{r.lahde}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="nappi nappi--paa" disabled={tallentaa}
                  onClick={() => void tallenna()}>
            {tallentaa ? t("pu.tallennetaan") : t("tas.tallenna_valitut")}
          </button>
        </>
      )}
    </div>
  );
}
